import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useToast } from '@/hooks/use-toast';

/**
 * PushNotificationsManager - Global component for managing push notifications
 * 
 * This component is mounted in App.tsx at the root level to ensure push notification
 * listeners persist across navigation and route changes. Previously, the push hook
 * was in Home.tsx which would unmount during redirects (onboarding/check-in),
 * causing the registration listener to be removed before the token could arrive.
 * 
 * This component:
 * 1. Watches for authenticated user
 * 2. Loads worker profile ID
 * 3. Initializes push notifications with stable listeners
 */
export const PushNotificationsManager = () => {
  const [workerId, setWorkerId] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Handler for notification taps
  const handleNotificationTap = useCallback((data: Record<string, string>) => {
    console.log('[PushManager] Notification tapped with data:', data);
    
    if (data?.type === 'corroboration_request' && data?.report_id) {
      navigate(`/corroborate/${data.report_id}`);
    } else if (data?.type === 'quest_assigned') {
      navigate('/quests');
    } else if (data?.type === 'team_alert') {
      toast({ title: "Peringatan Keselamatan", description: "Ada kondisi berbahaya di area kerja Anda." });
      navigate('/home');
    } else if (data?.type === 'incident_alert' && data?.incident_id) {
      sessionStorage.setItem('pendingIncidentAlert', JSON.stringify(data));
      navigate('/home?showIncidentAlert=true');
    } else if (data?.type === 'badge_earned') {
      navigate('/profile');
    } else if (data?.type === 'supervisor_task_assigned') {
      navigate('/supervisor?tab=tasks');
    }
  }, [navigate, toast]);

  // Initialize push notifications with workerId
  usePushNotifications(workerId, handleNotificationTap);

  // Load worker profile when auth state changes
  useEffect(() => {
    const loadWorkerProfile = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        setWorkerId(null);
        return;
      }

      const { data: profile } = await supabase
        .from('worker_profiles')
        .select('id')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (profile?.id) {
        console.log('[PushManager] Worker profile loaded:', profile.id);
        setWorkerId(profile.id);
      } else {
        setWorkerId(null);
      }
    };

    loadWorkerProfile();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[PushManager] Auth state changed:', event);
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        loadWorkerProfile();
      } else if (event === 'SIGNED_OUT') {
        setWorkerId(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // This component doesn't render anything visible
  return null;
};
